import { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Button } from 'antd';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined, UserOutlined, ShopOutlined, AppstoreOutlined,
  ShoppingCartOutlined, FileTextOutlined, DollarOutlined, CreditCardOutlined,
  HomeOutlined, MenuFoldOutlined, MenuUnfoldOutlined, HeartOutlined,
  SunOutlined, MoonOutlined, LogoutOutlined, SafetyOutlined,
} from '@ant-design/icons';
import { useTheme } from '../contexts/ThemeContext';
import { getUser } from '../utils/auth';

import AdminDashboard from '../pages/admin/AdminDashboard';
import AdminUsers from '../pages/admin/AdminUsers';
import AdminBusinesses from '../pages/admin/AdminBusinesses';
import AdminServices from '../pages/admin/AdminServices';
import AdminOrders from '../pages/admin/AdminOrders';
import AdminReports from '../pages/admin/AdminReports';
import TariffsPage from '../pages/admin/TariffsPage';
import PaymentsPage from '../pages/admin/PaymentsPage';
import '../pages/admin/admin.css';

const { Header, Content, Sider } = Layout;

const PAGES = {
  '/admin': { title: 'Boshqaruv paneli', sub: 'Umumiy statistika va ko\'rsatkichlar' },
  '/admin/users': { title: 'Foydalanuvchilar', sub: 'Foydalanuvchilarni boshqarish' },
  '/admin/businesses': { title: 'Bizneslar', sub: 'Ro\'yxatdan o\'tgan bizneslar' },
  '/admin/services': { title: 'Xizmatlar', sub: 'Platformadagi barcha xizmatlar' },
  '/admin/orders': { title: 'Buyurtmalar', sub: 'Buyurtmalarni nazorat qilish' },
  '/admin/reports': { title: 'Shikoyatlar', sub: 'Foydalanuvchi shikoyatlari' },
  '/admin/tariffs': { title: 'Tariflar', sub: 'Obuna tariflari boshqaruvi' },
  '/admin/payments': { title: 'To\'lovlar', sub: 'To\'lovlar tarixi' },
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const user = getUser();

  const menuItems = [
    {
      type: 'group', label: 'Asosiy',
      children: [
        { key: '/admin', icon: <DashboardOutlined />, label: 'Statistika' },
      ],
    },
    {
      type: 'group', label: 'Boshqaruv',
      children: [
        { key: '/admin/users', icon: <UserOutlined />, label: 'Foydalanuvchilar' },
        { key: '/admin/businesses', icon: <ShopOutlined />, label: 'Bizneslar' },
        { key: '/admin/services', icon: <AppstoreOutlined />, label: 'Xizmatlar' },
        { key: '/admin/orders', icon: <ShoppingCartOutlined />, label: 'Buyurtmalar' },
      ],
    },
    {
      type: 'group', label: 'Nazorat va moliya',
      children: [
        { key: '/admin/reports', icon: <FileTextOutlined />, label: 'Shikoyatlar' },
        { key: '/admin/tariffs', icon: <DollarOutlined />, label: 'Tariflar' },
        { key: '/admin/payments', icon: <CreditCardOutlined />, label: "To'lovlar" },
      ],
    },
  ];

  const profileMenu = {
    items: [
      { key: 'tax', icon: <SafetyOutlined />, label: 'Soliq / Havf tahlili' },
      { key: 'site', icon: <HomeOutlined />, label: 'Saytga qaytish' },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: 'Chiqish', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'site') navigate('/');
      if (key === 'tax') navigate('/admingov/tax/risk');
      if (key === 'logout') {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        navigate('/auth/login');
      }
    },
  };

  const page = PAGES[location.pathname] || { title: 'Admin', sub: '' };

  return (
    <div className="admin-root">
      <Layout style={{ minHeight: '100vh' }} hasSider>
        <Sider
          className="admin-sider"
          width={244}
          collapsedWidth={72}
          collapsed={collapsed}
          theme={isDark ? 'dark' : 'light'}
          trigger={null}
        >
          <div className="admin-brand">
            <div className="admin-brand-logo"><HeartOutlined /></div>
            {!collapsed && (
              <div className="admin-brand-text">
                <b>Quvonchli</b>
                <span>Admin Panel</span>
              </div>
            )}
          </div>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
          />
        </Sider>

        <Layout>
          <Header className="admin-header">
            <div className="admin-header-left">
              <button className="admin-collapse-btn" onClick={() => setCollapsed(!collapsed)}>
                {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </button>
              <div>
                <h1 className="admin-header-title">{page.title}</h1>
              </div>
            </div>
            <div className="admin-header-right">
              <Button
                shape="circle"
                icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                onClick={toggleTheme}
              />
              <Dropdown menu={profileMenu} trigger={['click']} placement="bottomRight">
                <div className="admin-userchip">
                  <Avatar size={32} style={{ background: 'linear-gradient(135deg,#5F33E1,#8B5CF6)' }}>
                    {(user?.fullname || 'A').charAt(0).toUpperCase()}
                  </Avatar>
                  <div>
                    <div className="admin-userchip-name">{user?.fullname || 'Administrator'}</div>
                    <div className="admin-userchip-role">Administrator</div>
                  </div>
                </div>
              </Dropdown>
            </div>
          </Header>

          <Content className="admin-content">
            <div className="admin-content-inner">
              {page.sub && (
                <div className="admin-page-head">
                  <div>
                    <h2>{page.title}</h2>
                    <p>{page.sub}</p>
                  </div>
                </div>
              )}
              <Routes>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="businesses" element={<AdminBusinesses />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="tariffs" element={<TariffsPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="*" element={<AdminDashboard />} />
              </Routes>
            </div>
          </Content>
        </Layout>
      </Layout>
    </div>
  );
};

export default AdminLayout;
